import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames/bind';
import styles from './Upload.module.css';
import { nanoid } from '@reduxjs/toolkit';

import Uppy from '@uppy/core';
import Tus from '@uppy/tus';

import SimpleBar from 'simplebar-react';
import useSimpleBar from '../../hooks/useSimpleBar';

import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { useInterval } from '../../hooks/useInterval';
import { useSlice } from '../../hooks/useSlice';
import { usePage } from '../../hooks/usePage';
import useModal from '../../hooks/useModal';

import { mainApiSlice } from '../../services/main';
import { settingsApiSlice } from '../../services/settings';
import { apiSlice } from '../../services/api';
import { store } from '../../services/store';
import { driveApiSlice } from '../../services/drive';

import { setIsListChanged } from '../../features/mainSlice';
import { setCredentials } from '../../features/authSlice';

import { Options } from '../../types/library';
import { UpdatedResource } from '../../types/drive';

import { getAccessToken, getTokens, setToken } from '../../utils/storage';
import { isOfType } from '../../utils/typeGuard';
import { VITE_API_V3 } from '../../utils/util';

import {
  CANCEL_All,
  COMPLETE,
  LOADING,
  OPEN_UPLOAD_LIST,
  PROGRESS,
  RETRY,
  UPLOAD_CANCEL,
} from '../../constants';

const endpoint = `${VITE_API_V3}/common/resources/upload/tus`;
const responseMap = new Map();

interface Queue {
  id: string;
  name: string;
  percent: number;
  error?: boolean;
}

let uploadMsg = '';
let queue: Queue[] = [];
let removalQueue: any[] = [];
let isUploadError = false;
let isTokenExpired = false;

export const uppyDrive = new Uppy({
  autoProceed: true,
}).use(Tus, {
  removeFingerprintOnSuccess: true,
  async onBeforeRequest(req) {
    req.setHeader('Authorization', `Bearer ${getAccessToken()}`);
  },
  onShouldRetry(err, retryAttempt, options, next) {
    if (
      isOfType(err, 'originalResponse') &&
      err.originalResponse.getStatus() === 401
    ) {
      return true;
    }
    return next(err);
  },
  async onAfterResponse(req, res) {
    if (res.getStatus() === 200) {
      if (res.getBody() !== '') {
        responseMap.set(req.getURL(), JSON.parse(res.getBody()));
      }
    }
  },
  endpoint,
  limit: 1,
});

uppyDrive
  .on('file-added', ({ id, extension }) => {
    // console.log('file-added', id, extension);
    uppyDrive.setFileMeta(id, {
      orgExt: extension,
      relativePath: new Date().valueOf(),
    });
  })
  .on('files-added', (files) => {
    console.log('files-added', files);
    uploadMsg = 'uploading';
    const addedQueue = files.map(
      ({ id, name }: { id: string; name: string }) => ({
        id,
        name,
        percent: 0,
      })
    );
    queue = [...queue, ...addedQueue];
  })
  .on('file-removed', (file, reason) => {
    // console.log('file, reason : ', file, reason);
  })
  .on('upload-progress', (file, progress) => {
    // console.log('file, progress : ', file, progress, queue);
    const percent = Math.round(
      (progress.bytesUploaded / progress.bytesTotal) * 100
    );
    const hasInRemovalQueue = !!removalQueue.filter(({ id }) => id === file?.id)
      .length;
    let current;

    if (isOfType(file, 'id')) {
      if (!hasInRemovalQueue) {
        current = { id: file?.id, name: file?.name, percent };
        if (uploadMsg !== 'upload complete') {
          queue = [current, ...queue.filter(({ id }) => id !== file?.id)].sort(
            (a, b) => b.percent - a.percent
          );
        }
        if (percent === 100) {
          removalQueue.push(current);
          setTimeout(() => {
            queue = queue.filter(({ id }) => id !== file?.id);
          }, 800);
        } else if (uploadMsg === 'cancel upload') {
          queue = [];
          removalQueue = [];
          uploadMsg = 'upload complete';
        }
      }
    }
  })
  .on('upload-success', (file, res) => {
    console.log('upload-success : ', file, res);
    if (uploadMsg === 'cancel upload') {
      uppyDrive.cancelAll();
      queue = [];
      removalQueue = [];
      uploadMsg = 'upload complete';
    }
  })
  .on('complete', async (result: any) => {
    console.log('complete : ', result);
    const responseBodyList: any = [];
    result.successful.forEach((sussessful: any) => {
      responseBodyList.push(responseMap.get(sussessful.uploadURL));
      // queue = queue.filter(({ id }) => id !== sussessful.id);
    });

    if (result.failed.length) {
      result.failed.forEach((failed: any) => {
        uppyDrive.retryUpload(failed.id);
      });
    } else {
      const resourceIdList = responseBodyList.reduce(
        (acc: [], { data }: { data: UpdatedResource }) => [
          ...acc,
          data.resourceId,
        ],
        []
      );
      const { overwriteYn, folderId } = result.successful[0].meta;

      await store.dispatch(
        driveApiSlice.endpoints.submitResource.initiate({
          folderId,
          overwriteYn,
          resourceIdList,
        })
      );
      uploadMsg = 'upload complete';
      removalQueue = [];
    }
  })
  .on('upload-error', (file, err, res) => {
    console.log('upload-error : ', file, err, res);
    isUploadError = true;

    if (err.message.includes('401_2')) {
      isTokenExpired = true;
    } else {
      queue = queue.map((el) =>
        el.id === file?.id ? { ...el, error: true } : el
      );
    }
  });

export default function UploadDrive() {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();

  const [simplebarRef] = useSimpleBar();
  const { selectOptions } = useSlice();
  const { sortType, sortOrder }: Options = useAppSelector(selectOptions);
  const { pageChanged, initPageList } = usePage();
  const { toggleModal } = useModal();

  const [isOpenList, setIsOpenList] = useState(false);
  const [list, setList] = useState(queue);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isError, setIsError] = useState(isUploadError);

  const [getStorage] = mainApiSlice.useLazyGetStorageQuery();
  const [getSettingsStorage] =
    settingsApiSlice.useLazyGetSettingsStorageQuery();

  const listLen = list.length;

  const toggleList = () => setIsOpenList(!isOpenList);

  const location = useLocation();
  const folderId = location?.state?.folderId;

  const initPage = () => {
    if (pageChanged) {
      dispatch(pageChanged(0));
      dispatch(
        initPageList({
          folderId,
          page: 0,
          sortBy: sortType,
          sortDir: sortOrder,
          id: nanoid(),
        })
      );
    }
    try {
      getStorage();
      getSettingsStorage();
      dispatch(setIsListChanged(true));
    } catch (error) {
      console.error('Failed to initialize', console.error);
    }
  };

  const cancelUpload = (id?: string) => {
    if (id) {
      uppyDrive.removeFile(id);
      queue = queue.filter(({ id: qId }) => qId !== id);
      if (!queue.length) {
        uploadMsg = 'upload complete';
      }
    } else {
      if (uploadMsg !== 'upload complete') {
        uploadMsg = 'cancel upload';
      }
    }
  };

  const retryUpload = (id: string) => {
    uppyDrive.retryUpload(id);
  };

  useInterval(
    () => {
      if (!queue.length && uploadMsg === 'upload complete') {
        uppyDrive.cancelAll();
        setTimeout(() => {
          setList(queue);
          !isCompleted && setIsCompleted(true);
          toggleModal({
            name: 'upload-mydrive',
            id: 'upload-mydrive-progress',
            type: 'upload',
            isOpen: false,
          });
        }, 800);
        initPage();
      } else if (isUploadError) {
        setIsError(true);
      } else {
        setList(queue);
        isCompleted && setIsCompleted(false);
      }
    },
    !queue.length && uploadMsg === 'upload complete' ? null : 40
  );

  useEffect(() => {
    if (isError && isTokenExpired) {
      dispatch(apiSlice.endpoints.refreshToken.initiate(getTokens()))
        .unwrap()
        .then((res: any) => {
          const tokens = res.data;
          isUploadError = false;
          isTokenExpired = false;
          dispatch(setCredentials(tokens));
          setToken(tokens);
          setIsError(false);
        });
    }
  }, [isError]);

  return (
    <div className={cx('upload--container', 'drive')}>
      <div className={cx('upload_layer', { max: isOpenList })}>
        <div className={cx('title')}>
          <span
            role="button"
            className={cx('btn', 'upload_cancel')}
            title={CANCEL_All}
            onClick={() => cancelUpload()}
          >
            <span>{CANCEL_All}</span>
          </span>
          <h2>
            {PROGRESS}(<span>{listLen}</span>)
          </h2>
          {listLen > 2 && (
            <span
              role="button"
              className={cx('btn', 'upload_items_open')}
              aria-label={OPEN_UPLOAD_LIST}
              onClick={toggleList}
            >
              <i className={cx('upload_arrow', { open: isOpenList })}></i>
              <span className={cx('blind')}>{OPEN_UPLOAD_LIST}</span>
            </span>
          )}
        </div>

        <SimpleBar forceVisible={true} ref={simplebarRef}>
          <div className={cx('inner')}>
            <div className={cx('scroll_box')}>
              {list.map(({ id, name, percent, error }: any) => {
                const isComplete = percent === 100;

                return (
                  <div
                    key={id}
                    className={cx(
                      'upload_items',
                      { 'is--done': isComplete } /* 'is--retry' */
                    )}
                  >
                    <div className={cx('upload_filename')}>{name}</div>

                    <div className={cx('upload_status')}>
                      {!isComplete && (
                        <div
                          className={cx('btn', 'upload_cancel')}
                          title={UPLOAD_CANCEL}
                          onClick={() => cancelUpload(id)}
                        >
                          <span className={cx('blind')}>{UPLOAD_CANCEL}</span>
                        </div>
                      )}

                      <div className={cx('progress_bar')}>
                        <div
                          className={cx('progress', 'rail')}
                          style={{ width: '100%' }}
                        ></div>
                        <div
                          className={cx('progress', 'current')}
                          style={{ width: `${percent}%` }}
                        ></div>
                      </div>

                      <div className={cx('ctrl_status')}>
                        {!error ? (
                          <span className={cx('txt')}>
                            {!percent
                              ? LOADING
                              : isComplete
                              ? COMPLETE
                              : `${percent}%`}
                          </span>
                        ) : (
                          <span
                            role="button"
                            className={cx('btn', 'upload_retry')}
                            aria-label={RETRY}
                            onClick={() => retryUpload(id)}
                          >
                            <span>{RETRY}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </SimpleBar>
      </div>
    </div>
  );
}
